import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const MembershipSection = ({ onOpenModal }) => {
  const { t } = useLanguage();
  
  return (
    <section 
      id="membership"
      className="relative min-h-screen bg-[#030303] py-32 md:py-48 overflow-hidden"
      data-testid="membership-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #2bfecd 0.5px, transparent 0)`,
          backgroundSize: '80px 80px'
        }} />
      </div>
      
      {/* Ambient Glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#2bfecd] opacity-[0.03] blur-[150px] rounded-full" />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
        >
          {/* Subtitle */}
          <motion.p 
            className="text-xs tracking-[0.5em] uppercase text-[#2bfecd]/60 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            {t('membership.subtitle')}
          </motion.p>
          
          {/* Title */}
          <motion.h2 
            className="font-cinzel text-5xl md:text-7xl tracking-[0.15em] uppercase mb-10 text-white"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.3 }}
          >
            {t('membership.title')}
          </motion.h2>
          
          {/* Decorative Element */}
          <motion.div 
            className="flex items-center justify-center gap-4 mb-12"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <div className="w-20 h-px bg-gradient-to-r from-transparent to-[#2bfecd]/30" />
            <div className="w-3 h-3 rotate-45 border border-[#2bfecd]/40" />
            <div className="w-20 h-px bg-gradient-to-l from-transparent to-[#2bfecd]/30" />
          </motion.div>
          
          {/* Description */}
          <motion.p 
            className="text-base md:text-lg leading-[2] tracking-wide text-white/50 mb-16 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            {t('membership.description')}
          </motion.p>
          
          {/* CTA Button */}
          <motion.button
            onClick={onOpenModal}
            className="group relative px-16 py-6 border border-[#2bfecd]/30 text-[#2bfecd] text-xs tracking-[0.3em] uppercase font-cinzel overflow-hidden transition-all duration-700 hover:border-[#2bfecd] hover:shadow-[0_0_60px_rgba(43,254,205,0.3)]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.8 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="membership-cta-button"
          >
            <span className="relative z-10">{t('membership.form.submit')}</span>
            <div className="absolute inset-0 bg-[#2bfecd] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700" />
            <span className="absolute inset-0 flex items-center justify-center text-black opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100 z-20 tracking-[0.3em]">
              {t('membership.form.submit')}
            </span>
          </motion.button>
          
          {/* Limited Spots */}
          <motion.p 
            className="text-[10px] tracking-[0.3em] uppercase text-white/20 mt-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 1 }}
          >
            {t('membership.spots')}
          </motion.p>
          
          {/* Stats Grid */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24 pt-16 border-t border-white/5"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 1.2 }}
          >
            {[
              { number: '7 Days', label: 'Open' },
              { number: '90°', label: 'Heat' },
              { number: '3°', label: 'Cold' },
              { number: '∞', label: 'Sessions' },
            ].map((stat, index) => (
              <motion.div 
                key={stat.label} 
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 1.3 + index * 0.1 }}
              >
                <span className="block font-cinzel text-3xl md:text-4xl text-[#2bfecd] mb-2">
                  {stat.number}
                </span>
                <span className="text-[9px] tracking-[0.3em] uppercase text-white/30">
                  {stat.label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default MembershipSection;
